import {
  Box,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';

const defaultNotifications = {
  documents: false,
  reports: false,
  bulk_uploads: false,
  consolidation_failure: false,
};

const UserNotificationSection = () => {
  const dispatch = useDispatch();
  const [org, setOrg] = useState(null);
  const [notifications, setNotifications] = useState(defaultNotifications);

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const res = await apiClient.get('/organization');
        setOrg(res.data);
        setNotifications(res.data?.user?.notifications ?? defaultNotifications);
      } catch (err) {
        dispatch(showSnackbar({ message: err.message, severity: 'error' }));
      }
    };
    fetchOrg();
  }, [dispatch]);

  const updateSettings = async (updatedNotif = {}) => {
    try {
      const data = { notifications: { ...notifications, ...updatedNotif } };
      await apiClient.patch(`/users/${org.user._id}/email_notifications`, data);
      setNotifications(data.notifications);
      dispatch(showSnackbar({ message: 'Settings updated successfully!', severity: 'success' }));
    } catch (err) {
      dispatch(
        showSnackbar({
          message: err?.response?.data?.errors?.message || 'Failed to update settings',
          severity: 'error',
        }),
      );
    }
  };

  const handleToggleChange = (field) => {
    const updated = { ...notifications, [field]: !notifications[field] };
    setNotifications(updated);
    updateSettings(updated);
  };

  return (
    <Paper elevation={0} sx={{ p: '1rem' }}>
      <Typography variant="h6" sx={{ mb: '1.5rem' }}>
        Email Notifications
        <Divider />
      </Typography>
      <Grid container spacing={1.5} direction="column">
        <Grid size={4.5}>
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={notifications.documents} onChange={() => handleToggleChange('documents')} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Document-Related
                  <Tooltip title="When enabled, you will recieve email notifications for successful submissin of documents.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
            />
          </FormGroup>
        </Grid>
        <Grid size={4.5}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch checked={notifications.bulk_uploads} onChange={() => handleToggleChange('bulk_uploads')} />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Bulk Upload Job Completion
                  <Tooltip title="When enabled, you will be notified via email once the job has finished processing.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
            />
          </FormGroup>
        </Grid>
        <Grid size={4.5}>
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={notifications.reports} onChange={() => handleToggleChange('reports')} />}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Report Generation
                  <Tooltip title="When enabled, you will be notified via email once the job has finished processing.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
            />
          </FormGroup>
        </Grid>
        <Grid size={4.5}>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={notifications.consolidation_failure}
                  onChange={() => handleToggleChange('consolidation_failure')}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Consolidation Failure
                  <Tooltip title="When enabled, you will be notified via email if the document consolidation fails.">
                    <IconButton size="small">
                      <InfoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
              labelPlacement="start"
              sx={{ ml: 0, justifyContent: 'space-between', width: '100%' }}
            />
          </FormGroup>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default UserNotificationSection;
